import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { User } from '@supabase/supabase-js';

interface PrivateRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requiredRole?: string;
}

/**
 * Helper to check for a given role in Supabase user metadata.
 * Supports both flat and array-style roles for flexibility.
 */
function hasRole(user: User | undefined, role: string): boolean {
  if (!user) return false;
  const appMeta = user.app_metadata as Record<string, unknown> | undefined;
  const userMeta = user.user_metadata as Record<string, unknown> | undefined;

  // Role as string (flat role)
  if (appMeta?.role === role || userMeta?.role === role) return true;

  // Role in roles array
  if (Array.isArray(appMeta?.roles) && appMeta?.roles.includes(role)) return true;
  if (Array.isArray(userMeta?.roles) && userMeta?.roles.includes(role)) return true;

  // Special boolean flags for admin/superuser in user_metadata only
  if (
    (role === 'admin' && userMeta?.is_admin === true) ||
    (role === 'superuser' && userMeta?.is_superuser === true)
  ) {
    return true;
  }

  return false;
}

/**
 * Checks if the user is an admin or superuser.
 */
function isAdminUser(user: User | undefined): boolean {
  return hasRole(user, 'admin') || hasRole(user, 'superuser');
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requireAdmin = false,
  requiredRole,
}) => {
  const { session, loading } = useAuth();
  const location = useLocation();

  const user = session?.user;

  // Memoized role checks
  const isAdmin = useMemo(() => isAdminUser(user), [user]);
  const hasRequiredRole = useMemo(() => {
    if (!requiredRole) return true;
    return hasRole(user, requiredRole);
  }, [user, requiredRole]);

  // Wait for auth to load
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">Loading…</div>
    );
  }

  // Not logged in → push to sign-in, saving intended path
  if (!session) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Admin or role required but user doesn't have it
  if ((requireAdmin && !isAdmin) || !hasRequiredRole) {
    // Only show alert in browser (not during SSR/prerender)
    if (typeof window !== 'undefined' && window.alert) {
      window.alert('You do not have permission to view this page.');
    }
    return <Navigate to="/" replace />;
  }

  // Authorized
  return <>{children}</>;
};

export default PrivateRoute;
